import jsonpipe from 'jsonpipe'
import _ from 'lodash'
import {req} from 'nanoajax.js'

import {AuthStore} from '../auth'
import Actions from './Actions'
import Constants from './Constants'

const {MESSAGE_URL, MESSAGES_URL} = Constants

class MessageService {
   constructor() {
      this._locks = {}
   }

   locked(ref) {
      return undefined !== this._locks[ref]
   }

   cachePromise(ref) {
      return this._locks[ref]
   }

   cacheLock(ref, promise) {
      if (this.locked(ref)) {
         return this._locks[ref]
      }

      this._locks[ref] = promise

      return promise
         .then(resp => {
            delete this._locks[ref]

            return resp
         })
         .catch(resp => {
            delete this._locks[ref]
            throw resp
         })
   }

   write(nid, key, msg, propagateToStore) {
      // write: 'message:write',
      let url = MESSAGE_URL + '/' + nid + (key ? '/' + key : ''),
         payload = JSON.stringify(msg),
         headers = {Authorization: AuthStore.signV1('POST', url, payload)}

      return req
         .post(url, payload, headers)
         .then(([resp, _httpreq]) => {
            if (true === propagateToStore) {
               Actions.write(resp)
            }

            return resp
         })
         .catch(err => {
            switch (err.req.status) {
               // Access denied, or whatever
               case 400:
               case 401:
               case 403:
               case 404:
                  break

               default:
                  throw err
            }

            return err
         })
   }

   read(uuid, propagateToStore) {
      // read: 'message:read',
      let url = MESSAGE_URL + '/' + uuid,
         headers = {Authorization: AuthStore.signV1('GET', url, '')}

      return this.cacheLock(url, req.get(url, null, headers))
         .then(([resp, _httpreq]) => {
            if (true === propagateToStore) {
               Actions.read(resp)
            }

            return resp
         })
         .catch(err => {
            switch (err.req.status) {
               // Access denied, or whatever
               case 401:
               case 403:
               case 404:
                  break

               default:
                  throw err
            }

            return err
         })
   }

   publish(_nid, _messages) {
      // publish a message to a channel: 'messages:publish',
   }
}

class QueryService {
   // this is really not that different from stream except we close the stream
   // once all results are deliverd
   query(resource, query, onData, propagateToStore, streamStorage) {
      return new Promise(
         function(resolve, reject) {
            let stream,
               results = [],
               factory = this.streamFactory(resource, {...query, continuous: 'false'}),
               pushResults = data => {
                  if (data.meta || 'ping' === data) {
                     return
                  }

                  if (onData) {
                     onData(data)
                  }

                  if (true === propagateToStore && true === streamStorage) {
                     Actions.consume(resource, query, data)
                  }

                  results.push(data)
               }

            stream = factory(pushResults, {
               error: err => {
                  switch (stream.status) {
                     case 400:
                        reject(new Error('Input Error: ' + stream.responseText))
                        break

                     default:
                        reject(new Error(err))
                        break
                  }
               },
               complete: _status => {
                  if (true === propagateToStore && true !== streamStorage) {
                     Actions.fetch(resource, query, results)
                  }
                  let opts = {
                     'data-encoding': stream.getResponseHeader('X-Data-Encoding')
                  }

                  resolve({results, xhr: stream, opts})
               }
            })

            return stream
         }.bind(this)
      )
   }

   streamFactory(resource, {continuous, query, ...rest}) {
      // open:    'messages:stream:open',
      // consume: 'messages:stream:consume',
      let url = MESSAGES_URL + '/' + resource,
         params = {
            query:  window.encodeURI(query || ''),
            stream: true,
            continuous
         }

      if (rest['date.from']) {
         params['date.from'] = rest['date.from']
      }

      if (rest['date.to']) {
         params['date.to'] = rest['date.to']
      }

      url = url + _.reduce(params, (acc, v, k) => acc + (acc ? '&' : '?') + `${k}=${v}`, '')

      let headers = {
         Authorization: AuthStore.signV1('GET', url, '')
      }

      return (onData, opts) => {
         Actions.clear()

         return jsonpipe.flow(url, {
            success: (data, xhr) => {
               onData(data, xhr)
            },
            method:          'GET',
            withCredentials: false,
            headers:         headers,
            ...opts
         })
      }
   }

   stream(resource, query, onData, propagateToStore) {
      return new Promise(
         function(resolve, reject) {
            let stream,
               params = _.omit({...query, continuous: 'true'}, 'date.to'),
               factory = this.streamFactory(resource, params)

            let opts = {
               error: err => {
                  switch (stream.status) {
                     case 400:
                        reject(new Error('Input Error: ' + stream.responseText))
                        break

                     default:
                        reject(new Error(err))
                        break
                  }
               },
               complete: _status => {
                  this.close(stream)
               },
               onHeaders: status => {
                  console.log('stream headers', status)

                  let respOpts = {
                     'data-encoding': stream.getResponseHeader('X-Data-Encoding')
                  }

                  if ('OK' === status) {
                     resolve({xhr: stream, results: null, opts: respOpts})
                  } else {
                     reject(status)
                  }
               }
            }

            let onChunk = data => {
               if (data.meta || 'ping' === data) {
                  return
               }

               if (onData) {
                  onData(data)
               }

               if (true === propagateToStore) {
                  Actions.consume(resource, query, data)
               }

               _.map(stream._handlers, ({query, callback, limit}, k) => {
                  if (null === query || query(data)) {
                     callback(data)

                     if (-1 < limit) {
                        if (1 === limit) {
                           delete stream._handlers[k]
                        }

                        return {query, callback, limit: limit - 1}
                     }
                  }

                  return {query, callback, limit}
               })
            }

            stream = factory(onChunk, opts)

            stream._handlers = []

            stream.addMsgHandler = (query, callback) => {
               stream._handlers.push({query, callback, limit: -1})
            }

            stream.awaitMsg = (query, callback) => {
               stream._handlers.push({query, callback, limit: 1})
            }

            stream.removeMsgHandler = fun => {
               stream._handlers = _.filter(stream._handlers, ({callback}) => callback !== fun)
            }

            return stream
         }.bind(this)
      )
   }

   close(stream) {
      Actions.close(stream)
   }
}

const qs = new QueryService()
const ms = new MessageService()

export {ms as MessageService, qs as QueryService}



// WEBPACK FOOTER //
// lib/api/message/Service.js